import { HttpClient } from '@angular/common/http';
import { inject, Service, signal } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, finalize, map, shareReplay, tap } from 'rxjs/operators';
import { Response } from './local/Response';

/**
 * The clip engine (clip studio, the promo-clip track, the clip actions on recordings, the
 * brand kit) belongs to Promo Clips: it needs the in-house engine (`inhouseClips`, opt-in)
 * and Promo Clips not switched off (`promoClips` is opt-out, a missing key counts as on).
 */
export function isClipEngineEnabled(features: Record<string, boolean>): boolean {
  return features['inhouseClips'] === true && features['promoClips'] !== false;
}

/**
 * Thin client for the backend's per-tenant feature-flag endpoint
 * (GET /api/v1/tenant/features). Cached for the session; falls back to an empty
 * feature set on error so calling components gracefully hide their flagged UI.
 *
 * Mirrors the service of the same name in Hypecast.WebApp to keep both apps aligned
 * on a single source of truth served by the backend.
 */
@Service()
export class TenantFeatureService {
  private httpClient = inject(HttpClient);

  private readonly state = signal<Record<string, boolean>>({});
  /** The tenant's features; empty until the first load (and after a failed one). Templates follow it. */
  readonly features = this.state.asReadonly();
  private loaded = false;
  /** The request on its way: callers in the meantime share it instead of sending their own. */
  private inFlight: Observable<Record<string, boolean>> | null = null;

  load(): Observable<Record<string, boolean>> {
    if (this.loaded) {
      return of(this.state());
    }

    this.inFlight ??= this.httpClient.get<Response<Record<string, boolean>>>('tenant/features').pipe(
      map((response: Response<Record<string, boolean>>) => response.data),
      tap((features: Record<string, boolean>) => {
        this.state.set(features);
        this.loaded = true;
      }),
      catchError(() => {
        // Do NOT mark as loaded on error: a single network blip (e.g. a dropped
        // mobile connection -> status 0) must not latch the feature set to empty
        // for the whole session. Leaving `loaded` false lets the next load() retry
        // so flagged UI (e.g. the HypecastAI sidebar entry) reappears once the
        // backend is reachable again, instead of staying hidden until a full reload.
        return of({} as Record<string, boolean>);
      }),
      finalize(() => (this.inFlight = null)),
      shareReplay({ bufferSize: 1, refCount: false }),
    );
    return this.inFlight;
  }

  /** Reads the {@link features} signal: a template or `computed` asking this updates once they load. */
  isEnabled(feature: string): boolean {
    return this.state()[feature] ?? false;
  }
}
